import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import {
  CONSENT_DENIED,
  CONSENT_GRANTED,
  CONSENT_REOPEN_EVENT,
  applyConsent,
  isConsentRequiredRegion,
  readConsent,
  saveConsent,
} from '../../util/consent';

import NamedLink from '../NamedLink/NamedLink';

import css from './CookieConsent.module.css';

/**
 * Einwilligungsbanner für nicht notwendige Cookies — nur für Besuchende aus dem
 * EWR und dem Vereinigten Königreich. Wer aus der Schweiz oder dem übrigen
 * Ausland kommt, sieht es nicht; dort gilt die Widerspruchslösung aus
 * Abschnitt 5 der Datenschutzerklärung.
 *
 * SSR/Hydration: Der erste Client-Render muss dem Server-Markup entsprechen,
 * sonst wirft React #418/#423 und rendert die ganze Seite neu. Deshalb liefert
 * die Komponente initial `null` und entscheidet erst im useEffect nach dem
 * Mount, ob das Banner erscheint.
 *
 * @component
 * @returns {JSX.Element|null} Banner oder null
 */
const CookieConsent = () => {
  const headingId = useId();
  const [isVisible, setIsVisible] = useState(false);
  const acceptRef = useRef(null);
  const wasReopened = useRef(false);

  useEffect(() => {
    const stored = readConsent();

    // Frühere Entscheidung erneut melden: Der Stub tut das schon vor dem
    // Bundle, aber nach einer Client-Navigation ohne Reload ist gtag.js
    // eventuell erst danach geladen.
    if (stored) {
      applyConsent(stored);
      return undefined;
    }

    if (isConsentRequiredRegion()) {
      setIsVisible(true);
    }
    return undefined;
  }, []);

  useEffect(() => {
    const handleReopen = () => {
      wasReopened.current = true;
      setIsVisible(true);
    };
    window.addEventListener(CONSENT_REOPEN_EVENT, handleReopen);
    return () => window.removeEventListener(CONSENT_REOPEN_EVENT, handleReopen);
  }, []);

  useEffect(() => {
    // Nur beim bewussten Wiederöffnen den Fokus holen — beim ersten Seitenaufruf
    // würde ein Fokussprung die Person aus dem Inhalt reissen.
    if (isVisible && wasReopened.current && acceptRef.current) {
      acceptRef.current.focus();
      wasReopened.current = false;
    }
  }, [isVisible]);

  const decide = useCallback(decision => {
    saveConsent(decision);
    setIsVisible(false);
  }, []);

  if (!isVisible) {
    return null;
  }

  return (
    // Die sichtbare Überschrift ist zugleich der zugängliche Name des
    // Landmarks — kein zweiter, abweichender Text nur für Screenreader.
    <div className={css.root} role="region" aria-labelledby={headingId}>
      <div className={css.panel}>
        <div className={css.text}>
          <h2 id={headingId} className={css.title}>
            <FormattedMessage id="CookieConsent.title" />
          </h2>
          <p className={css.body}>
            <FormattedMessage
              id="CookieConsent.body"
              values={{
                privacyLink: (
                  <NamedLink name="PrivacyPolicyPage" className={css.inlineLink}>
                    <FormattedMessage id="CookieConsent.privacyLinkLabel" />
                  </NamedLink>
                ),
              }}
            />
          </p>
        </div>

        {/* Ablehnen ist genauso prominent wie Zustimmen — alles andere wäre
            nach den EDSA-Leitlinien keine freiwillige Einwilligung. */}
        <div className={css.actions}>
          <button
            type="button"
            className={css.buttonSecondary}
            onClick={() => decide(CONSENT_DENIED)}
          >
            <FormattedMessage id="CookieConsent.rejectLabel" />
          </button>
          <button
            type="button"
            ref={acceptRef}
            className={css.buttonPrimary}
            onClick={() => decide(CONSENT_GRANTED)}
          >
            <FormattedMessage id="CookieConsent.acceptLabel" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
